import { Component, Inject, Injector, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { BaseParamsRequest, DealCreateRequest, LeadCreateRequest, NoteCreateRequest, PersonCreateRequest, PipelineObject, StageListRequest, StageObject, TrendPipedriveAPIService } from '@trendbuild/trend-pipedrive-api';
import { UserObject } from '@trendbuild/trend-pipedrive-api/src/lib/models/users.types'
import { AbstractComponent, Attendance, Integration, MessageService } from 'lib-trend-core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from '../../../state/app.state';
import { integrationsSelector } from '../../../state/selectors';
import { CrmIntegrationsComponent } from '../crm-integrations.component';

interface ItemOption {
  label: string;
  value: string;
}

@Component({
    selector: 'crm-pipedrive.component',
    templateUrl: './crm-pipedrive.v2.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CrmPipedriveComponent extends AbstractComponent {
  private trendPipedriveAPIService: TrendPipedriveAPIService = new TrendPipedriveAPIService();

  readonly integrations$: Observable<Array<Integration>> = this.store.select(integrationsSelector);

  step: number = 0;
  apiToken: ItemOption[] = [];
  selectedIntegrationApiToken: string;
  pipelinesOptions: ItemOption[] = [];
  selectedPipeline: string | null = null;
  stagesOptions: ItemOption[] = [];
  usersOptions: ItemOption[] = [];
  selectedStage: string | null = null;
  selectedUser: { label: string; value: number } | null = null;
  dealValue: number | null = null;
  sendMessageHistory: boolean = false;
  dialogRef: MatDialogRef<any>;

  constructor(
    injector: Injector,
    private messageService: MessageService,
    public dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public attendance: Attendance,
    private matDialogRef: MatDialogRef<CrmPipedriveComponent>,
    private store: Store<AppState>
  ) {
    super(injector)
    this.formGroup = this.formBuilder.group({
      sendMessageHistory: [false],
    })
  }

  ngOnInit() {
    this.loadIntegrations();
  }

  override ngOnDestroy() {
    this.loadUsersOptions();
  }

  onPipelineSelection(event: MatSelectChange) {
    const selectElement = event.value;
    this.apiToken = selectElement;
  }

  usersSelection(event: MatSelectChange) {
    const selectElement = event.value;
    this.selectedUser = selectElement;
  }

  integrationSelection(event: MatSelectChange) {
    this.selectedIntegrationApiToken = event.value;

    if (this.selectedIntegrationApiToken) {
      this.loadUsersOptions();
      this.loadPipelines();
    }
  }

  async loadIntegrations() {
    try {
      const companyId = this.attendance.company._id || this.attendance.company || this.attendance;
      if (!companyId) {
        this.alertService.error(this.t('attendance.crmPipedrive.companyIdMissing'));
        return;
      }

      this.integrations$.subscribe(value => {
        if (!value || value.length === 0) {
          this.alertService.warning(
            this.t('attendance.crmPipedrive.noActiveIntegration')
          );
          return;
        }

        this.apiToken = value.map((integration: Integration) => ({
          label: integration.name,
          value: integration.metadata['api_token'],
        }));

      });

    } catch (error) {
      console.error('Erro ao carregar integrações:', error);
      this.alertService.error(this.t('attendance.crmPipedrive.fetchIntegrationError'));
      return;
    }
  }

  async loadUsersOptions() {
    try {
      const companyId = this.attendance.company._id || this.attendance.company;
      if (!companyId) {
        this.alertService.error(this.t('attendance.crmPipedrive.companyIdMissing'));
        return;
      }

      const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

      // if (!integration) {
      //   this.alertService.error('Integração não encontrada.');
      //   return;
      // }

      const baseParamsRequest: BaseParamsRequest = {
        api_token: integration.value,
      };

      const users = await this.trendPipedriveAPIService.listUsers(
        baseParamsRequest
      );

      if (!users.success || !users.data) {
        this.alertService.error(this.t('attendance.crmPipedrive.fetchUsersError'));
        this.matDialogRef.close();
        return;
      }

      const activeUsers = users.data.filter((user: UserObject) => user.active_flag);

      this.usersOptions = activeUsers.map((user: UserObject) => ({
        label: user.name,
        value: String(user.id),
      }));
    } catch (error) {
      console.error('Error loading user options:', error);
      // this.alertService.error(this.t('attendance.crmPipedrive.fetchIntegrationError'));
    }
  }

  async loadPipelines() {
    try {
      const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

      const pipelinesResult = await this.trendPipedriveAPIService.listPipelines(
        {
          api_token: integration.value
        }
      );

      if (pipelinesResult.success && pipelinesResult.data) {
        this.pipelinesOptions = pipelinesResult.data.map((pipeline: PipelineObject) => ({
          value: pipeline.id.toString(),
          label: pipeline.name,
        }));
      }
    } catch (error) {
      console.error('Error loading pipelines:', error);
      this.alertService.error(this.t('attendance.crmPipedrive.loadPipelinesError'));
    }
  }

  async stageSelect() {
    if (!this.selectedPipeline) {
      this.stagesOptions = [];
      return;
    }

    try {
      const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

      const stageListRequest: StageListRequest = {
        api_token: integration.value,
        pipeline_id: parseInt(this.selectedPipeline),
      };

      const stagesResult = await this.trendPipedriveAPIService.listStages(
        stageListRequest
      );

      if (stagesResult.success && stagesResult.data) {
        this.stagesOptions = stagesResult.data.map((stage: StageObject) => ({
          value: String(stage.id),
          label: stage.name,
        }));
      }
    } catch (error) {
      console.error('Error loading stages:', error);
      this.alertService.error(this.t('attendance.crmPipedrive.loadStagesError'));
    }
  }

  async sendDataPersonDeal() {
    const historyMessage = this.formGroup.get('sendMessageHistory')?.value;
    const contactName = this.attendance?.contact?.name || 'Cliente Desconhecido';
    const contactPhone = this.attendance?.contact?.phone || '11999999999';

    if (!this.selectedUser || !this.selectedPipeline || !this.selectedStage) {
      this.alertService.warning(this.t('attendance.crmPipedrive.selectAllFields'));
      return
    }

    const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

    this.loading = true
    const personCreateRequest: PersonCreateRequest = {
      api_token: integration.value,
      name: contactName,
      phone: [{ value: contactPhone }],
      owner_id: Number(this.selectedUser.value),
    };


    const dealCreateRequest: DealCreateRequest = {
      api_token: integration.value,
      title: `${this.attendance?.contact?.name || 'Contato Sem Nome'}`,
      pipeline_id: parseInt(this.selectedPipeline),
      stage_id: parseInt(this.selectedStage),
      user_id: this.selectedUser.value,
    };

    try {
      let noteCreateRequest: NoteCreateRequest | null = null;

      if (historyMessage) {
        const messages = await this.messageService
          .getByAttendance(this.attendance._id)
          .toPromise();

        const messageHistory = messages
          .map((msg) => {
            const sender =
              !msg.user && !msg.automated
                ? `<b>${this.attendance?.contact?.name}</b>`
                : `<b>Plataforma</b>`

            return `${sender}: ${msg.content}\n`
          })
          .join('<br>');

        noteCreateRequest = {
          api_token: integration.value,
          content: `Histórico de mensagens:<br><br>${messageHistory}`,
          user_id: String(this.selectedUser.value),
          add_time: new Date().toISOString().split('.')[0].replace('T', ' '),
        };

        const result = await this.trendPipedriveAPIService.createPersonAndDeal(
          personCreateRequest,
          dealCreateRequest,
          noteCreateRequest
        );

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataWithHistorySentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadWithHistoryProblem'))
        }
      } else {
        const result = await this.trendPipedriveAPIService.createPersonAndDeal(
          personCreateRequest,
          dealCreateRequest
        );

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataSentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadWithoutHistoryProblem'))
        }
      }
    } catch (error) {
      console.error('Erro ao enviar dados:', error);
      this.alertService.error(this.t('attendance.crmPipedrive.processSendDataFailure'));
    }
  }

  async sendDataPersonLead() {
    const historyMessage = this.formGroup.get('sendMessageHistory')?.value;
    const contactName = this.attendance?.contact?.name || 'Cliente Desconhecido';
    const contactPhone = this.attendance?.contact?.phone || '11999999999';

    if (!this.selectedUser) {
      this.alertService.warning(
        this.t('attendance.crmPipedrive.selectResponsibleBeforeSend')
      );
      return;
    }

    const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

    this.loading = true
    const personCreateRequest: PersonCreateRequest = {
      api_token: integration.value,
      name: contactName,
      phone: [{ value: contactPhone }],
      owner_id: Number(this.selectedUser.value),
    };


    const leadCreateRequest: LeadCreateRequest = {
      api_token: integration.value,
      title: `${contactName}`,
      owner_id: Number(this.selectedUser.value),
    };


    const noteCreateRequest: NoteCreateRequest = {
      api_token: integration.value,
      content: '',
      user_id: String(this.selectedUser.value),
      add_time: new Date().toISOString().split('.')[0].replace('T', ' '),
    };

    try {
      if (historyMessage) {
        const messages = await this.messageService.getByAttendance(this.attendance._id).toPromise();

        const messageHistory = messages
          .map((msg) => {
            const sender =
              !msg.user && !msg.automated
                ? `<b>${this.attendance?.contact?.name}</b>`
                : `<b>Plataforma</b>`

            return `${sender}: ${msg.content}\n`
          })
          .join('<br>');

        noteCreateRequest.content = `Histórico de mensagens:<br><br>${messageHistory}`

        const result = await this.trendPipedriveAPIService.createPersonAndLead(
          personCreateRequest,
          leadCreateRequest,
          noteCreateRequest
        )

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataWithHistorySentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      } else {
        const result = await this.trendPipedriveAPIService.createPersonAndLead(
          personCreateRequest,
          leadCreateRequest
        )

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataSentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      }
    } catch (error) {
      console.error('Erro ao enviar dados:', error)
      this.alertService.error(this.t('attendance.crmPipedrive.processSendDataFailure'))
    }
  }

  async sendDataPerson() {
    const historyMessage = this.formGroup.get('sendMessageHistory')?.value
    const contactName = this.attendance?.contact?.name || 'Cliente Desconhecido'
    const contactPhone = this.attendance?.contact?.phone || '11999999999'

    if (!this.selectedUser) {
      this.alertService.warning(
        this.t('attendance.crmPipedrive.selectResponsibleBeforeSend')
      )
      return
    }

    const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

    this.loading = true
    const personCreateRequest: PersonCreateRequest = {
      api_token: integration.value,
      name: contactName,
      phone: [{ value: contactPhone }],
      owner_id: Number(this.selectedUser.value),
    }

    try {
      if (historyMessage) {
        const createPerson = await this.trendPipedriveAPIService.createPersonId(
          personCreateRequest
        );

        const noteCreateRequest: NoteCreateRequest = {
          api_token: integration.value,
          content: '',
          user_id: String(this.selectedUser.value),
          person_id: Number(createPerson.data.id),
          add_time: new Date().toISOString().split('.')[0].replace('T', ' '),
        };

        const messages = await this.messageService
          .getByAttendance(this.attendance._id)
          .toPromise()

        const messageHistory = messages
          .map((msg) => {
            const sender =
              !msg.user && !msg.automated
                ? `<b>${this.attendance?.contact?.name}</b>`
                : `<b>Plataforma</b>`

            return `${sender}: ${msg.content}\n`
          })
          .join('<br>')

        noteCreateRequest.content = `Histórico de mensagens:<br><br>${messageHistory}`

        const result = await this.trendPipedriveAPIService.createNote(
          noteCreateRequest
        )

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataWithHistorySentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      } else {
        const result = await this.trendPipedriveAPIService.createPersonId(
          personCreateRequest
        )

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataSentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      }
    } catch (error) {
      console.error('Erro ao enviar dados:', error)
      this.alertService.error(this.t('attendance.crmPipedrive.processSendDataFailure'))
    }
  }

  async sendDataDeal() {
    const historyMessage = this.formGroup.get('sendMessageHistory')?.value

    if (!this.selectedPipeline || !this.selectedStage || !this.selectedUser) {
      this.alertService.warning(this.t('attendance.crmPipedrive.fillAllFields'))
      return
    }

    this.loading = true
    const companyId = this.attendance?.company?._id || this.attendance?.company
    const params = {
      filters: {
        company: companyId,
        type: 'PIPEDRIVE',
        actived: true,
        removed: false,
      },
    }

    const integration = this.apiToken.find((token) => token.value === this.selectedIntegrationApiToken);

    const dealCreateRequest: DealCreateRequest = {
      api_token: integration.value,
      title: `${this.attendance?.contact?.name || 'Contato Sem Nome'}`,
      pipeline_id: parseInt(this.selectedPipeline),
      stage_id: parseInt(this.selectedStage),
      user_id: this.selectedUser.value,
      value: this.dealValue || 0,
      expected_close_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split('T')[0],
    }

    try {
      if (historyMessage) {
        const result = await this.trendPipedriveAPIService.createDealId(
          dealCreateRequest
        )

        const noteCreateRequest: NoteCreateRequest = {
          api_token: integration.value,
          content: '',
          user_id: String(this.selectedUser.value),
          deal_id: Number(result.data.id),
          add_time: new Date().toISOString().split('.')[0].replace('T', ' '),
        }

        const messages = await this.messageService
          .getByAttendance(this.attendance._id)
          .toPromise()

        const messageHistory = messages
          .map((msg) => {
            const sender =
              !msg.user && !msg.automated
                ? `<b>${this.attendance?.contact?.name}</b>`
                : `<b>Plataforma</b>`

            return `${sender}: ${msg.content}\n`
          })
          .join('<br>')

        noteCreateRequest.content = `Histórico de mensagens:<br><br>${messageHistory}`

        const nota = await this.trendPipedriveAPIService.createNote(
          noteCreateRequest
        )

        if (nota) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataWithHistorySentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      } else {
        const result = await this.trendPipedriveAPIService.createDealId(
          dealCreateRequest
        )

        if (result) {
          this.alertService.success(this.t('attendance.crmPipedrive.dataSentOk'))
          this.step = 2
          this.loading = false
        } else {
          this.loading = false
          this.alertService.warning(this.t('attendance.crmPipedrive.createLeadProblem'))
        }
      }
    } catch (error) {
      console.error('Erro ao enviar dados:', error)
      this.alertService.error(this.t('attendance.crmPipedrive.processSendDataFailure'))
    }
  }

  openDialogCrm(attendance: Attendance): void {

    if (this.matDialogRef) {
      this.matDialogRef.close();
    }

    this.dialog.open(CrmIntegrationsComponent, {
      width: '850px',
      data: attendance
    });

  }

  nextStepLeadsOne() {
    this.step = 1
  }

  nextStepLeadsTwo() {
    this.step = 2
  }

  nextStepLeadsThree() {
    this.step = 3
  }

  nextStepLeadsFour() {
    this.step = 4
  }

  nextStepLeadsFive() {
    this.step = 5
  }

  nextStepLeadsSix() {
    this.step = 6
  }

  clearIntegrationBack() {
    this.step = 0
  }

  clearValuesToBack() {
    this.step = 2
  }

  sendHistoryChange(event: Event) {
    this.sendMessageHistory = (event.target as HTMLInputElement).checked;
  }

}
